const sheet = new CSSStyleSheet();
sheet.replaceSync(`
  :host {
    overflow: hidden;
  }
  :host > ::slotted([data-track]) {
    width: 100%;
    overflow: hidden;
  }
`);

class LabSlider extends HTMLElement {
  constructor() {
    super();

    this.attachShadow({mode: 'open'});
    this.shadowRoot.adoptedStyleSheets = [sheet];
    this.shadowRoot.innerHTML = `<slot></slot>`;

    this.handlePreviousClickBound = this.handlePreviousClick.bind(this);
    this.handleNextClickBound = this.handleNextClick.bind(this);
    // Separate from handleNextClickBound so setInterval's timer-driven advances don't report as
    // a 'click' content_interaction (see emitInteraction) - only a real next-button click does.
    this.handleAutoRotateTickBound = () => this.handleNextClick(true);
    this.handleDotClickBound = this.handleDotClick.bind(this);
    this.handleScrollBound = this.handleScroll.bind(this);
    this.sliderId = this.id || null;
    this.lastInteractionType = null;
    // Behavior for the next active-slide scroll; set 'instant' to skip the CSS smooth scroll.
    this.scrollBehavior = 'smooth';
  }

  connectedCallback() {
    this.render();
  }

  disconnectedCallback() {
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.handlePreviousClickBound);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.handleNextClickBound);
    }

    if (this.rotationInterval) {
      clearInterval(this.rotationInterval);
    }

    if (this.pagination.length > 0) {
      this.pagination.forEach((dot) => dot.removeEventListener('click', this.handleDotClickBound));
    }

    if (this.handleScrollBound && this.slidesList) {
      this.slidesList.removeEventListener('scroll', this.handleScrollBound);
    }
  }

  static get observedAttributes() {
    return ['active-slide'];
  }

  attributeChangedCallback(name, _, newValue) {
    if (name !== 'active-slide') return;
    if (this.hasAttribute('data-skip-active-slide')) return;

    this.goToSlide(newValue, this.scrollBehavior);
    this.scrollBehavior = 'smooth'; // reset; an override applies to one scroll only
  }

  static isTouchDevice() {
    return (
      window.matchMedia('(pointer: coarse)').matches ||
      window.matchMedia('(max-width: 1023px)').matches
    );
  }

  render() {
    this.slidesList = this.querySelector('ul');
    this.sliderCount = this.querySelector('[data-slider-count]');

    if (!this.slidesList) {
      console.error("Error initializing LabSlider component: Missing 'ul' element.");

      return;
    }

    // Check if this is a vertical slider
    this.isVertical =
      this.hasAttribute('data-direction') && this.getAttribute('data-direction') === 'vertical';

    this.initChildElements();
    if (!this.slides || !this.slides.length) {
      // Silently exit if no slides found - this is not an error condition
      // Some sliders may be conditionally rendered or have no content
      return;
    }

    this.prevButton = this.querySelector('button[data-action="previous"]');
    this.nextButton = this.querySelector('button[data-action="next"]');
    this.pagination = this.querySelectorAll('button[data-action="pagination"]');

    this.currentSlideIndex = Number(this.getAttribute('active-slide')) || 0;
    this.isSetToAutoRotate = this.getAttribute('auto-rotate') || 'false';
    this.sliderDurationInSecond = Number(this.getAttribute('slider-duration')) || 5;

    this.updateActiveSlideIndex();
    this.updateSlidesInView();
    this.updatePagination();

    if (this.isSetToAutoRotate === 'true') {
      this.mediaQuery = window.matchMedia('(max-width: 767px)');

      if (this.mediaQuery.matches) {
        this.startAutoRotating();
        return;
      }

      // Start auto-rotation for desktop if there are multiple slides
      if (this.slideCount > 1) {
        this.startAutoRotating();
      }
    }

    if (this.prevButton && this.nextButton) {
      this.initActionButtons();
    }

    if (this.pagination.length > 0) {
      this.initPagination();
    }

    this.enableScrollDetection();
  }

  // Sliders that bleed into the page padding set scroll-padding; that space isn't room for slides.
  scrollPadding(side) {
    return parseFloat(getComputedStyle(this.slidesList)[`scrollPadding${side}`]) || 0;
  }

  updateSlidesInView() {
    requestAnimationFrame(() => {
      if (this.isVertical) {
        const containerHeight = this.slidesList.clientHeight;
        const slideHeight = this.slides[0].offsetHeight;

        // Check if this slider uses gap-based calculation
        const gapData = this.getAttribute('data-gap');
        if (gapData) {
          const gap = parseInt(gapData, 10);
          let totalSlideHeight = 0;
          for (let i = 0; i < this.slides.length; i += 1) {
            totalSlideHeight += this.slides[i].offsetHeight;
          }
          const averageSlideHeight = totalSlideHeight / this.slides.length;
          const actualSlideHeight = averageSlideHeight + gap;
          this.slidesInView = Math.floor(containerHeight / actualSlideHeight);
        } else {
          // Original calculation for existing sliders without data-gap
          this.slidesInView = Math.floor(containerHeight / slideHeight);
        }
      } else {
        const containerWidth =
          this.slidesList.clientWidth - this.scrollPadding('Left') - this.scrollPadding('Right');
        const slideWidth = this.slides[0].offsetWidth;

        // Check if this slider uses gap-based calculation
        const gapData = this.getAttribute('data-gap');
        if (gapData) {
          const gap = parseInt(gapData, 10);
          let totalSlideWidth = 0;
          for (let i = 0; i < this.slides.length; i += 1) {
            totalSlideWidth += this.slides[i].offsetWidth;
          }
          const averageSlideWidth = totalSlideWidth / this.slides.length;
          const actualSlideWidth = averageSlideWidth + gap;
          this.slidesInView = Math.floor(containerWidth / actualSlideWidth);
        } else {
          // Original calculation for existing sliders without data-gap
          this.slidesInView = Math.floor(containerWidth / slideWidth) || 0;
        }
      }
    });
  }

  startAutoRotating() {
    const sliderDurationInMs = this.sliderDurationInSecond * 1000;

    this.rotationInterval = setInterval(this.handleAutoRotateTickBound, sliderDurationInMs);
  }

  goToSlide(slideIndex, behavior = 'smooth') {
    if (!this?.slides?.[slideIndex]) return;

    this.programmaticScrollTarget = Number(slideIndex);
    clearTimeout(this.programmaticScrollClearTimer);
    this.programmaticScrollClearTimer = setTimeout(() => {
      this.programmaticScrollTarget = null;
    }, 700);

    if (this.isVertical) {
      const scrollPosition = this.slides[slideIndex].offsetTop - this.slidesList.offsetTop;

      this.slidesList.scrollTo({
        top: scrollPosition,
        behavior,
      });
    } else {
      const scrollPosition =
        this.slides[slideIndex].offsetLeft -
        this.slidesList.offsetLeft -
        this.scrollPadding('Left');

      this.slidesList.scrollTo({
        left: scrollPosition,
        behavior,
      });
    }

    if (this.sliderCount) {
      this.sliderCount.textContent = `${parseInt(slideIndex, 10) + 1} / ${this.slideCount}`;
    }
  }

  updateActiveSlideIndex() {
    this.setAttribute('active-slide', this.currentSlideIndex);
    this.updateActiveSlideUI();
  }

  updateActiveSlideUI() {
    if (this.hasAttribute('data-skip-active-slide')) return;

    this.slides.forEach((slide) => slide.removeAttribute('data-active-slide'));
    this.slides[this.currentSlideIndex].setAttribute('data-active-slide', '');

    if (this.sliderCount) {
      this.sliderCount.textContent = `${this.currentSlideIndex + 1} / ${this.slideCount}`;
    }

    if (this.isSetToAutoRotate === 'false') {
      if (this.prevButton) {
        this.prevButton.disabled = this.currentSlideIndex === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = this.currentSlideIndex === this.slideCount - this.slidesInView;
      }
    }

    this.updatePagination();

    document.dispatchEvent(
      new CustomEvent(`lab-slider:slider-updated-${this.sliderId}`, {
        detail: {index: this.currentSlideIndex},
      })
    );
  }

  initActionButtons() {
    this.prevButton.addEventListener('click', this.handlePreviousClickBound);
    this.nextButton.addEventListener('click', this.handleNextClickBound);
  }

  handlePreviousClick() {
    this.lastInteractionType = 'button';
    if (this.currentSlideIndex - 1 < 0) {
      this.currentSlideIndex = this.slideCount - this.slidesInView;
    } else {
      this.currentSlideIndex -= 1;
    }

    this.updateActiveSlideIndex();
    this.emitInteraction();
  }

  handleNextClick(isAutoRotate = false) {
    this.lastInteractionType = 'button';
    if (this.currentSlideIndex + 1 > this.slideCount - this.slidesInView) {
      this.currentSlideIndex = 0;
    } else {
      this.currentSlideIndex += 1;
    }

    this.updateActiveSlideIndex();
    this.emitInteraction(isAutoRotate);
  }

  initPagination() {
    this.pagination.forEach((dot) => dot.addEventListener('click', this.handleDotClickBound));
  }

  initChildElements() {
    this.slides = this.slidesList.querySelectorAll(':scope > li');
    this.slideCount = this.slides.length;
  }

  updatePagination() {
    if (!this.pagination.length) return;

    for (let i = 0; i < this.slideCount; i += 1) {
      if (i === this.currentSlideIndex) {
        this.pagination[i].querySelector('svg').classList.add('active--dot');
      } else {
        this.pagination[i].querySelector('svg').classList.remove('active--dot');
      }
    }
  }

  handleDotClick(event) {
    this.currentSlideIndex = Number(event.currentTarget.getAttribute('data-pagination-index'));
    this.updateActiveSlideIndex();
    this.updatePagination();
  }

  enableScrollDetection() {
    if (this.slideCount <= 1) return;

    this.slidesList.addEventListener('scroll', this.handleScrollBound, {passive: true});
  }

  handleScroll() {
    if (this.scrollHandleRaf) return;

    this.scrollHandleRaf = requestAnimationFrame(() => {
      this.scrollHandleRaf = null;
      const newIndex = this.getCurrentVisibleSlideIndex();
      if (this.programmaticScrollTarget != null) {
        if (newIndex === this.programmaticScrollTarget) {
          this.programmaticScrollTarget = null;
          clearTimeout(this.programmaticScrollClearTimer);
        }
        return;
      }
      if (newIndex === this.currentSlideIndex || newIndex < 0 || newIndex >= this.slideCount) {
        return;
      }
      this.currentSlideIndex = newIndex;
      this.updateActiveSlideUI();

      this.dispatchEvent(
        new CustomEvent('dragging:stopped', {
          detail: {index: this.currentSlideIndex},
          bubbles: true,
        })
      );

      if (this.dataset.trackSection) {
        this.dispatchEvent(
          new CustomEvent('content_interaction', {
            detail: {
              interaction_type: 'swipe',
              section: this.dataset.trackSection,
              item_detail: this.currentSlideIndex,
            },
            bubbles: true,
          })
        );
      }
    });
  }

  getCurrentVisibleSlideIndex() {
    if (this.isVertical) {
      const {scrollTop} = this.slidesList;
      const slideHeight = this.slides[0].offsetHeight;
      const gap = this.getSlideGap();
      const slideWithGap = slideHeight + gap;

      return Math.round(scrollTop / slideWithGap);
    }

    const {scrollLeft} = this.slidesList;
    const slideWidth = this.slides[0].offsetWidth;
    const gap = this.getSlideGap();
    const slideWithGap = slideWidth + gap;

    return Math.round(scrollLeft / slideWithGap);
  }

  getSlideGap() {
    if (this.slides.length < 2) return 0;

    // Calculate actual gap between slides
    if (this.isVertical) {
      const firstSlideBottom = this.slides[0].offsetTop + this.slides[0].offsetHeight;
      const secondSlideTop = this.slides[1].offsetTop;
      return secondSlideTop - firstSlideBottom;
    }

    const firstSlideRight = this.slides[0].offsetLeft + this.slides[0].offsetWidth;
    const secondSlideLeft = this.slides[1].offsetLeft;
    return secondSlideLeft - firstSlideRight;
  }

  emitInteraction(isAutoRotate = false) {
    this.dispatchEvent(
      new CustomEvent('slider:interaction', {
        detail: {
          index: this.currentSlideIndex,
          interaction: this.lastInteractionType || 'unknown',
        },
        bubbles: true,
      })
    );

    // Timer-driven autorotation isn't a shopper click - counting it here would inflate click
    // metrics for any tracked slider that also auto-rotates.
    if (this.dataset.trackSection && !isAutoRotate) {
      this.dispatchEvent(
        new CustomEvent('content_interaction', {
          detail: {
            interaction_type: 'click',
            section: this.dataset.trackSection,
            item_detail: this.currentSlideIndex,
          },
          bubbles: true,
        })
      );
    }
  }
}

customElements.define('lab-slider', LabSlider);
